import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Star, LogOut, LayoutDashboard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HomePage = () => {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reviews, setReviews] = useState([]);
  const [news, setNews] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const [reviewsRes, newsRes, companiesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reviews/recent?limit=10`),
        axios.get(`${BACKEND_URL}/api/news?limit=5`),
        axios.get(`${BACKEND_URL}/api/companies`)
      ]);
      setReviews(reviewsRes.data);
      setNews(newsRes.data);
      setCompanies(companiesRes.data);
    } catch (error) {
      toast.error(t('toast.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLogout = async () => {
    await logout();
    toast.success(t('toast.loggedOut'));
  };

  const renderStars = (rating) => {
    return (
      <div className="flex gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-4 h-4 ${star <= rating ? 'fill-yellow-500 text-yellow-500' : 'text-slate-300'}`}
          />
        ))}
      </div>
    );
  };

  const homeStructuredData = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "ForexForum - Recensioni Broker Forex",
    "description": "Forum italiano con oltre 8000 recensioni verificate su broker forex e trading online",
    "numberOfItems": companies.length,
    "itemListElement": companies.slice(0, 10).map((c, i) => ({
      "@type": "ListItem",
      "position": i + 1,
      "item": {
        "@type": "Organization",
        "name": c.name,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": c.average_rating,
          "reviewCount": c.review_count,
          "bestRating": 5,
          "worstRating": 1
        }
      }
    }))
  }), [companies]);

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO
        title="Recensioni Broker Forex Affidabili - AWStocks, Interactive Invests, App-Coins"
        description="ForexForum: oltre 8000 recensioni verificate su broker forex. Scopri opinioni reali su AWStocks, Interactive Invests, App-Coins.com. Confronta spread, commissioni e servizi."
        keywords="recensioni broker forex, opinioni broker trading, AWStocks recensioni, Interactive Invests opinioni, App-Coins recupero fondi, migliori broker forex Italia, broker forex affidabili, spread bassi, commissioni forex"
        structuredData={homeStructuredData}
      />
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center">
            <h1 className="text-2xl font-black text-blue-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('home.title')}
            </h1>
          </Link>
          <div className="flex gap-3 items-center">
            <LanguageSwitcher />
            {user ? (
              <>
                <span className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {user.name}
                </span>
                {user.role === 'admin' ? (
                  <Button
                    onClick={() => navigate('/admin')}
                    data-testid="admin-dashboard-btn"
                    className="bg-blue-900 text-white hover:bg-blue-800"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {t('common.admin')}
                  </Button>
                ) : (
                  <Button
                    onClick={() => navigate('/dashboard')}
                    data-testid="client-dashboard-btn"
                    className="bg-blue-900 text-white hover:bg-blue-800"
                  >
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    Dashboard
                  </Button>
                )}
                <Button
                  onClick={handleLogout}
                  data-testid="logout-btn"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </Button>
              </>
            ) : (
              <>
                <Button
                  onClick={() => navigate('/login')}
                  data-testid="login-btn"
                  variant="outline"
                  className="border-slate-300 text-slate-700 hover:bg-slate-50"
                >
                  {t('common.login')}
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="register-btn"
                  className="bg-blue-900 text-white hover:bg-blue-800"
                >
                  {t('common.signUp')}
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-blue-900 via-blue-800 to-blue-900 text-white py-16">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-5xl lg:text-6xl font-black tracking-tighter mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('home.heroTitle')}
          </h2>
          <p className="text-lg text-blue-100 max-w-2xl mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('home.heroSubtitle')}
          </p>
          <Button
            onClick={() => navigate('/companies')}
            data-testid="browse-companies-btn"
            className="bg-white text-blue-900 hover:bg-blue-50 font-bold text-lg px-8 py-6"
          >
            {t('home.browseAll')}
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Quick Category Overview */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-black text-green-800 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {companies.filter(c => c.average_rating >= 4.5).length}
            </div>
            <div className="text-sm text-green-700 font-bold">{t('home.excellent')}</div>
            <div className="text-xs text-green-600">⭐ 4.5+</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-black text-blue-800 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {companies.filter(c => c.average_rating >= 4.0 && c.average_rating < 4.5).length}
            </div>
            <div className="text-sm text-blue-700 font-bold">{t('home.recommended')}</div>
            <div className="text-xs text-blue-600">⭐ 4.0-4.5</div>
          </div>
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-black text-yellow-800 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {companies.filter(c => c.average_rating >= 3.0 && c.average_rating < 4.0).length}
            </div>
            <div className="text-sm text-yellow-700 font-bold">{t('home.average')}</div>
            <div className="text-xs text-yellow-600">⭐ 3.0-4.0</div>
          </div>
          <div className="bg-gradient-to-br from-red-50 to-red-100 border border-red-200 rounded-lg p-6 text-center">
            <div className="text-3xl font-black text-red-800 mb-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {companies.filter(c => c.average_rating < 2.0).length}
            </div>
            <div className="text-sm text-red-700 font-bold">{t('home.avoid')}</div>
            <div className="text-xs text-red-600">🚫 &lt;2.0</div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Reviews Feed - 2/3 width */}
          <div className="lg:col-span-2 space-y-6">
            <h3 className="text-3xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('home.recentReviews')}
            </h3>
            
            {loading ? (
              <div className="text-slate-600">{t('common.loading')}</div>
            ) : reviews.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-8 text-center">
                <p className="text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('home.noReviews')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div
                    key={review.id}
                    data-testid={`review-card-${review.id}`}
                    className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    <Link
                      to={`/company/${review.company_id}`}
                      data-testid={`company-link-${review.company_id}`}
                      className="text-xl font-bold text-blue-900 hover:text-blue-800 transition-colors"
                      style={{ fontFamily: 'Chivo, sans-serif' }}
                    >
                      {review.company_name}
                    </Link>
                    <div className="flex items-center gap-3 mt-2 mb-3">
                      {renderStars(review.rating)}
                      <span className="text-sm text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {t('home.by')} {review.reviewer_name}
                      </span>
                    </div>
                    <p className="text-slate-700 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      {review.comment}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* News Sidebar - 1/3 width */}
          <div className="space-y-6">
            <h3 className="text-2xl font-black text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {t('home.latestNews')}
            </h3>
            
            {loading ? (
              <div className="text-slate-600">{t('common.loading')}</div>
            ) : news.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-lg p-6">
                <p className="text-slate-600 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  {t('home.noNews')}
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {news.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.slug || item.id}`}
                    data-testid={`news-card-${item.id}`}
                    className="block bg-white border border-slate-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200"
                  >
                    {item.image_url && (
                      <img
                        src={item.image_url}
                        alt={item.title}
                        className="w-full h-32 object-cover"
                      />
                    )}
                    <div className="p-4">
                      <h4 className="font-bold text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                        {item.title}
                      </h4>
                      <p className="text-sm text-slate-600 line-clamp-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        {item.content}
                      </p>
                      <span className="inline-block mt-2 text-xs font-bold text-blue-700 hover:text-blue-900">
                        {t('home.readArticle')}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* SEO Content Section */}
      <section className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white border border-slate-200 rounded-lg p-8 shadow-sm">
          <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('home.featuredTitle')}
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <article>
              <h3 className="text-lg font-bold text-blue-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.awstocksTitle')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.awstocksDesc')}
              </p>
            </article>
            <article>
              <h3 className="text-lg font-bold text-blue-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.iiTitle')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.iiDesc')}
              </p>
            </article>
            <article>
              <h3 className="text-lg font-bold text-blue-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.appCoinsTitle')}</h3>
              <p className="text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.appCoinsDesc')}
              </p>
            </article>
          </div>

          <h2 className="text-2xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('home.faqTitle')}
          </h2>
          <div className="space-y-4" data-testid="faq-section">
            <details className="border border-slate-200 rounded-lg p-4">
              <summary className="font-bold text-slate-800 cursor-pointer" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.faq1q')}</summary>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.faq1a')}
              </p>
            </details>
            <details className="border border-slate-200 rounded-lg p-4">
              <summary className="font-bold text-slate-800 cursor-pointer" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.faq2q')}</summary>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.faq2a')}
              </p>
            </details>
            <details className="border border-slate-200 rounded-lg p-4">
              <summary className="font-bold text-slate-800 cursor-pointer" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.faq3q')}</summary>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.faq3a')}
              </p>
            </details>
            <details className="border border-slate-200 rounded-lg p-4">
              <summary className="font-bold text-slate-800 cursor-pointer" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.faq4q')}</summary>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.faq4a')}
              </p>
            </details>
            <details className="border border-slate-200 rounded-lg p-4">
              <summary className="font-bold text-slate-800 cursor-pointer" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.faq5q')}</summary>
              <p className="mt-2 text-sm text-slate-600 leading-relaxed" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.faq5a')}
              </p>
            </details>
          </div>
        </div>
      </section>

      {/* Footer with SEO links */}
      <footer className="bg-blue-900 text-white py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
              <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>ForexForum</h3>
              <p className="text-sm text-blue-200" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                {t('home.footerDesc')}
              </p>
            </div>
            <nav aria-label="Broker in evidenza">
              <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.featuredBrokers')}</h3>
              <ul className="space-y-1 text-sm text-blue-200">
                <li><Link to="/company/69d8bd3d2768ad07db838e65" className="hover:text-white transition-colors">AWStocks</Link></li>
                <li><Link to="/company/69d8bd3d2768ad07db838fa6" className="hover:text-white transition-colors">Interactive Invests</Link></li>
                <li><Link to="/company/69f0eb38e47297259b76fd37" className="hover:text-white transition-colors">App-Coins.com</Link></li>
                <li><Link to="/companies" className="hover:text-white transition-colors">{t('home.allBrokers')}</Link></li>
              </ul>
            </nav>
            <nav aria-label="Categorie">
              <h3 className="font-bold text-lg mb-3" style={{ fontFamily: 'Chivo, sans-serif' }}>{t('home.categories')}</h3>
              <ul className="space-y-1 text-sm text-blue-200">
                <li><Link to="/companies" className="hover:text-white transition-colors">{t('home.excellentBrokers')}</Link></li>
                <li><Link to="/companies" className="hover:text-white transition-colors">{t('home.recommendedBrokers')}</Link></li>
                <li><Link to="/companies" className="hover:text-white transition-colors">{t('home.avoidBrokers')}</Link></li>
                <li><Link to="/companies" className="hover:text-white transition-colors">{t('home.fundRecovery')}</Link></li>
              </ul>
            </nav>
          </div>
          <div className="border-t border-blue-800 pt-6 text-center text-xs text-blue-300">
            <p>{t('home.copyright')}</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
